import { useCallback, type MouseEvent } from 'react';
import { css } from 'styled-system/css';
import { ArrowIcon, ARROW_COLOR, arrowLabel } from '@/ui/components/grid/ClueArrowIcon';
import { LetterPreview } from '@/ui/components/grid/LetterPreview';
import type { Clue } from '@/ui/components/grid/useGridNavigation';
import { t } from '@/ui/i18n';

// Fixed total height: outer banner is the ceiling; LetterPreview / alt chip intrinsically exceed minHeight.
const banner = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  paddingInline: '10px',
  paddingBlock: '8px',
  height: '80px',
  bg: 'surfaceElevated',
  borderRadius: '10px',
  border: '1px solid token(colors.border)',
});

// Each row claims exactly half the banner via flex; overflow clipped so children cannot resize the outer.
const block = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
});

const altBlockTappable = css({
  cursor: 'pointer',
  touchAction: 'manipulation',
  background: 'none',
  border: 'none',
  padding: '0',
  font: 'inherit',
  color: 'inherit',
  textAlign: 'left',
  width: '100%',
  _active: { transform: 'scale(0.99)' },
});

// Inline style for the color since Panda extracts only literal tokens or same-module string literals — see Cell.tsx letterArrowBase.
const arrowGlyph = css({
  flexShrink: 0,
  width: '20px',
  height: '20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': { width: '14px', height: '14px' },
});

const arrowGlyphMuted = css({ color: 'fgMuted' });

const clueText = css({
  flex: 1,
  minWidth: 0,
  fontSize: '13px',
  color: 'fg',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const clueTextMuted = css({ color: 'fgMuted' });

const empty = css({
  flex: 1,
  fontSize: '13px',
  color: 'fgMuted',
  fontStyle: 'italic',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

// Invisible placeholder; same flex sizing as a real block so it shares half the banner.
const blockPlaceholder = css({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  minHeight: 0,
  overflow: 'hidden',
  visibility: 'hidden',
});

export interface ClueBannerProps {
  readonly clue: Clue | null;
  readonly alternateClue: Clue | null;
  readonly onToggleDirection: () => void;
  readonly getEntryAt: (row: number, col: number) => string;
  readonly focusedPosition: { row: number; col: number } | null;
  readonly isCellValidated?: (row: number, col: number) => boolean;
}

// One-glyph placeholder slot used inside the invisible alt row so it matches a real row's height.
function AltPlaceholderRow() {
  return (
    <div className={blockPlaceholder} aria-hidden>
      <span className={`${arrowGlyph} ${arrowGlyphMuted}`}>&nbsp;</span>
      <span className={`${clueText} ${clueTextMuted}`}>&nbsp;</span>
    </div>
  );
}

export function ClueBanner({
  clue,
  alternateClue,
  onToggleDirection,
  getEntryAt,
  focusedPosition,
  isCellValidated,
}: ClueBannerProps) {
  // Preserve the focused cell on tap: suppress mousedown's implicit focus shift.
  const handleAltMouseDown = useCallback((e: MouseEvent) => e.preventDefault(), []);
  if (!clue) {
    return (
      <div className={banner} aria-live="off">
        <div className={block}>
          <span className={empty}>{t('clueBanner.empty')}</span>
        </div>
        <AltPlaceholderRow />
      </div>
    );
  }
  return (
    <div className={banner}>
      <div className={block}>
        <span
          className={arrowGlyph}
          style={{ color: ARROW_COLOR }}
          role="img"
          aria-label={t('clueBanner.aria.definition', { direction: arrowLabel[clue.clue.arrow] })}
        >
          <ArrowIcon arrow={clue.clue.arrow} />
        </span>
        <span className={clueText} title={clue.clue.text}>
          {clue.clue.text}
        </span>
        <LetterPreview
          cells={clue.cells}
          focusedPosition={focusedPosition}
          getEntryAt={getEntryAt}
          isCellValidated={isCellValidated}
        />
      </div>
      {alternateClue ? (
        <button
          type="button"
          className={`${block} ${altBlockTappable}`}
          aria-label={t('clueBanner.aria.toggle', { direction: arrowLabel[alternateClue.clue.arrow] })}
          onMouseDown={handleAltMouseDown}
          onClick={onToggleDirection}
        >
          <span
            className={`${arrowGlyph} ${arrowGlyphMuted}`}
            role="img"
            aria-label={t('clueBanner.aria.alternate', { direction: arrowLabel[alternateClue.clue.arrow] })}
          >
            <ArrowIcon arrow={alternateClue.clue.arrow} />
          </span>
          <span className={`${clueText} ${clueTextMuted}`} title={alternateClue.clue.text}>
            {alternateClue.clue.text}
          </span>
          <LetterPreview
            cells={alternateClue.cells}
            focusedPosition={focusedPosition}
            getEntryAt={getEntryAt}
            isCellValidated={isCellValidated}
            muted
          />
        </button>
      ) : (
        <AltPlaceholderRow />
      )}
    </div>
  );
}
